import React, { useEffect, useState } from "react";
import Select from "react-select";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { academylist, feedashboard } from "../../../services/admin";
import { stuacademicyearlist } from "../../../services/master";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels
);

export default function FeeDashboard() {
  const isSuperAdmin = localStorage.getItem("Role") === "1";

  const [aclist, setACList] = useState([]);
  const [acaid, setAcaID] = useState("");
  const [alist, setAList] = useState([]);
  const [ayear, setAYear] = useState("");

  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const inr = (n) =>
    "₹" + Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });

  function loadYears(aid) {
    const temp = new FormData();
    if (isSuperAdmin && aid !== "") {
      temp.append("academy_id", aid.value);
    }
    stuacademicyearlist(temp).then((res) => {
      if (res.status === true) {
        setAList(
          (res.data || []).map((item) => ({ value: item.id, label: item.aca_year }))
        );
      }
    });
  }

  function loadDashboard(aid, year) {
    setLoading(true);
    const temp = new FormData();
    if (isSuperAdmin && aid !== "") {
      temp.append("academy_id", aid.value);
    }
    if (year !== "") {
      temp.append("academic_year", year);
    }
    feedashboard(temp).then((res) => {
      setLoading(false);
      if (res && res.status === true) {
        setSummary(res.data);
      } else {
        setSummary(null);
      }
    });
  }

  useEffect(() => {
    if (isSuperAdmin) {
      academylist().then((res) => {
        if (res.status === true && res.data.details.length > 0) {
          const opts = res.data.details.map((d) => ({
            value: d.id,
            label: d.name + " - " + d.code,
          }));
          setACList(opts);

          // Default to the logged-in academy (like other pages do).
          const def = localStorage.getItem("AAcademy");
          const match =
            def != null ? opts.find((o) => String(o.value) === String(def)) : null;
          if (match) {
            setAcaID(match);
            loadYears(match);
            loadDashboard(match, "");
          } else {
            loadDashboard("", "");
            loadYears("");
          }
        }
      });
    } else {
      loadDashboard("", "");
      loadYears("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleAcademy(e) {
    const val = e || "";
    setAcaID(val);
    setAYear("");
    setAList([]);
    loadYears(val);
    loadDashboard(val, "");
  }

  function handleYear(e) {
    const val = e ? e.target.value : "";
    setAYear(val);
    loadDashboard(acaid, val);
  }

  const cards = [
    { label: "Total Billed", value: inr(summary?.total_billed), cls: "bg-primary" },
    { label: "Collected", value: inr(summary?.total_collected), cls: "bg-success" },
    { label: "Pending", value: inr(summary?.total_pending), cls: "bg-danger" },
    { label: "Discount", value: inr(summary?.total_discount), cls: "bg-warning" },
  ];

  const byClass = summary?.by_class || [];
  const byFeetype = summary?.by_feetype || [];
  const byMonth = summary?.by_month || [];

  const classChart = {
    labels: byClass.map((c) => c.class_name || "—"),
    datasets: [
      { label: "Billed", data: byClass.map((c) => Number(c.billed || 0)), backgroundColor: "#2d4ac6" },
      { label: "Collected", data: byClass.map((c) => Number(c.collected || 0)), backgroundColor: "#28a745" },
      { label: "Pending", data: byClass.map((c) => Number(c.pending || 0)), backgroundColor: "#dc3545" },
    ],
  };

  // Doughnut: share of Collected amount across fee types (single metric).
  const donutPalette = [
    "#2d4ac6", "#28a745", "#ffc107", "#dc3545", "#17a2b8",
    "#6f42c1", "#fd7e14", "#20c997", "#e83e8c", "#6610f2",
  ];
  const feetypeChart = {
    labels: byFeetype.map((f) => f.fee_type || "Other"),
    datasets: [
      {
        label: "Collected",
        data: byFeetype.map((f) => Number(f.collected || 0)),
        backgroundColor: byFeetype.map((_, i) => donutPalette[i % donutPalette.length]),
        borderWidth: 1,
      },
    ],
  };

  // Always show Jan–Dec; bucket the backend rows ("YYYY-MM") by month-of-year.
  const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthBuckets = MONTHS.map(() => ({ billed: 0, collected: 0, pending: 0 }));
  byMonth.forEach((m) => {
    const mi = parseInt(String(m.month || "").split("-")[1], 10) - 1;
    if (mi >= 0 && mi < 12) {
      monthBuckets[mi].billed += Number(m.billed || 0);
      monthBuckets[mi].collected += Number(m.collected || 0);
      monthBuckets[mi].pending += Number(m.pending || 0);
    }
  });

  // Grouped bars (side-by-side) so equal Billed/Collected values stay distinct.
  const monthChart = {
    labels: MONTHS,
    datasets: [
      { label: "Billed", data: monthBuckets.map((b) => b.billed), backgroundColor: "#2d4ac6" },
      { label: "Collected", data: monthBuckets.map((b) => b.collected), backgroundColor: "#28a745" },
      { label: "Pending", data: monthBuckets.map((b) => b.pending), backgroundColor: "#dc3545" },
    ],
  };

  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: "bottom" }, datalabels: { display: false } },
  };

  // Horizontal grouped bar (bars run left-to-right).
  const horizontalOptions = { ...baseOptions, indexAxis: "y" };

  // Doughnut: labels drawn OUTSIDE each slice with a leader line + color swatch.
  const doughnutOptions = {
    ...baseOptions,
    layout: { padding: { left: 90, right: 90, top: 16, bottom: 16 } },
    plugins: {
      legend: { display: true, position: "bottom" },
      datalabels: { display: false },
    },
  };

  // Custom plugin: draws a leader line (with an arrowhead at the slice) out to a
  // label showing the colour swatch + fee type + amount.
  const doughnutOutLabels = {
    id: "doughnutOutLabels",
    afterDraw(chart) {
      const ctx = chart.ctx;
      const ds = chart.data.datasets[0];
      if (!ds) return;
      const total = ds.data.reduce((s, v) => s + Number(v || 0), 0);
      const meta = chart.getDatasetMeta(0);
      meta.data.forEach((arc, i) => {
        const value = Number(ds.data[i] || 0);
        if (!value) return;
        const pct = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
        const { x, y, outerRadius, startAngle, endAngle } = arc.getProps(
          ["x", "y", "outerRadius", "startAngle", "endAngle"],
          true
        );
        const mid = (startAngle + endAngle) / 2;
        const right = Math.cos(mid) >= 0;
        const color = Array.isArray(ds.backgroundColor)
          ? ds.backgroundColor[i]
          : ds.backgroundColor;
        const label = chart.data.labels[i] || "";

        const sx = x + Math.cos(mid) * outerRadius;
        const sy = y + Math.sin(mid) * outerRadius;
        const ex = x + Math.cos(mid) * (outerRadius + 14);
        const ey = y + Math.sin(mid) * (outerRadius + 14);
        const hx = ex + (right ? 26 : -26);

        ctx.save();
        ctx.strokeStyle = color;
        ctx.fillStyle = color;
        ctx.lineWidth = 1.5;
        // leader line: slice -> elbow -> horizontal
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(ex, ey);
        ctx.lineTo(hx, ey);
        ctx.stroke();
        // arrowhead pointing into the slice
        const ah = 5;
        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(sx - Math.cos(mid - 0.45) * ah, sy - Math.sin(mid - 0.45) * ah);
        ctx.lineTo(sx - Math.cos(mid + 0.45) * ah, sy - Math.sin(mid + 0.45) * ah);
        ctx.closePath();
        ctx.fill();
        // colour swatch + text beside it
        const sw = 9;
        const swatchX = right ? hx + 4 : hx - 4 - sw;
        ctx.fillRect(swatchX, ey - sw / 2, sw, sw);
        ctx.font = "11px sans-serif";
        ctx.textBaseline = "middle";
        ctx.textAlign = right ? "left" : "right";
        ctx.fillStyle = "#333";
        const tx = right ? swatchX + sw + 5 : swatchX - 5;
        ctx.fillText(`${label}  ${inr(value)} (${pct}%)`, tx, ey);
        ctx.restore();
      });
    },
  };


  return (
    <div>
      <div className="page-header">
        <h2 className="main-content-title tx-24 mg-b-5">Fee Dashboard</h2>
        <ol className="breadcrumb">
          <li className="breadcrumb-item">
            <a>Fee Module</a>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Fee Dashboard
          </li>
        </ol>
      </div>

      <div className="content-body">
        <div className="container-fluid">
          {/* Filters */}
          <div className="card">
            <div className="card-body">
              <div className="row">
                {isSuperAdmin && (
                  <div className="col-xl-4 col-sm-6">
                    <div className="mb-3">
                      <label className="form-label">Select Academy</label>
                      <Select
                        className="default-select"
                        placeholder="Select Academy"
                        value={acaid}
                        options={aclist}
                        onChange={handleAcademy}
                        isSearchable
                        isClearable
                      />
                    </div>
                  </div>
                )}
                <div className="col-xl-4 col-sm-6">
                  <div className="mb-3">
                    <label className="form-label">Academic Year</label>
                    <select
                      className="default-select form-control wide"
                      value={ayear}
                      onChange={handleYear}
                    >
                      <option value="">All Years</option>
                      {alist.map((item) => (
                        <option value={item.value} key={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary cards */}
          <div className="row mt-2">
            {cards.map((c) => (
              <div className="col-xl-3 col-sm-6" key={c.label}>
                <div className={`card text-white ${c.cls}`}>
                  <div className="card-body">
                    <h6 className="text-white mb-1">{c.label}</h6>
                    <h3 className="text-white mb-0">{loading ? "…" : c.value}</h3>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="row mt-2">
            <div className="col-xl-4 col-sm-6">
              <div className="card">
                <div className="card-body">
                  <h6 className="mb-1">Collection %</h6>
                  <h3 className="mb-0">
                    {loading ? "…" : (summary?.collection_percent ?? 0) + "%"}
                  </h3>
                </div>
              </div>
            </div>
            <div className="col-xl-4 col-sm-6">
              <div className="card">
                <div className="card-body">
                  <h6 className="mb-1">Students with Fees</h6>
                  <h3 className="mb-0">{loading ? "…" : summary?.student_count ?? 0}</h3>
                </div>
              </div>
            </div>
            <div className="col-xl-4 col-sm-6">
              <div className="card">
                <div className="card-body">
                  <h6 className="mb-1">Fee Records</h6>
                  <h3 className="mb-0">{loading ? "…" : summary?.fee_rows ?? 0}</h3>
                </div>
              </div>
            </div>
          </div>

          {/* Fee-type-wise amount cards (same layout as the All Fee page) */}
          <h4 className="mt-3 mb-2">Fee Type Wise Collection</h4>
          <div className="row mt-1">
            {summary && summary.by_feetype && summary.by_feetype.length > 0 ? (
              summary.by_feetype.map((row, index) => (
                <div className="col-xl-4 col-md-4" key={index}>
                  <div className="card custom-card">
                    <div className="card-bodyy">
                      <div className="row">
                        <div className="col-lg-12">
                          <div className="product-content">
                            <h3 className="title">
                              <a>{row.fee_type || "Other"}</a>
                            </h3>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row sales-product-infomation pb-0 mb-0 mx-auto wd-100p">
                      <div className="col-6 justify-content-center text-center">
                        <h3 className="mb-1 font-weight-bold">
                          <a>₹ {Number(row.billed || 0).toFixed(2)}</a>
                          <p className="text-muted">Total</p>
                        </h3>
                      </div>
                      <div className="col-6 justify-content-center text-center">
                        <h3 className="mb-1 font-weight-bold">
                          <a>₹ {Number(row.collected || 0).toFixed(2)}</a>
                          <p className="text-muted">Paid</p>
                        </h3>
                      </div>
                    </div>
                    <div className="row sales-product-infomation pb-0 mb-0 mx-auto wd-100p">
                      <div className="col-6 justify-content-center text-center">
                        <h3 className="mb-1 font-weight-bold">
                          <a>₹ {Number(row.discount || 0).toFixed(2)}</a>
                          <p className="text-muted">Discount</p>
                        </h3>
                      </div>
                      <div className="col-6 justify-content-center text-center">
                        <h3 className="mb-1 font-weight-bold">
                          <a>₹ {Number(row.pending || 0).toFixed(2)}</a>
                          <p className="text-muted">Pending</p>
                        </h3>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12">
                <div className="card">
                  <div className="card-body text-center text-muted">
                    {loading ? "Loading…" : "No fee data found"}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Charts */}
          <div className="row mt-3">
            <div className="col-xl-6">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title mb-0 text-white">Class Wise Fees</h4>
                </div>
                <div className="card-body">
                  <div style={{ height: "300px" }}>
                    {byClass.length > 0 ? (
                      <Bar data={classChart} options={horizontalOptions} />
                    ) : (
                      <p className="text-center text-muted mt-5">
                        {loading ? "Loading…" : "No data"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-6">
              <div className="card">
                <div className="card-header">
                  <h4 className="card-title mb-0 text-white">Fee Type Wise (Collected)</h4>
                </div>
                <div className="card-body">
                  <div style={{ height: "300px" }}>
                    {byFeetype.length > 0 ? (
                      <Doughnut data={feetypeChart} options={doughnutOptions} plugins={[doughnutOutLabels]} />
                    ) : (
                      <p className="text-center text-muted mt-5">
                        {loading ? "Loading…" : "No data"}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="col-xl-12">
              <div className="card mt-2">
                <div className="card-header">
                  <h4 className="card-title mb-0 text-white">Month Wise Fees</h4>
                </div>
                <div className="card-body">
                  <div style={{ height: "300px" }}>
                    <Bar data={monthChart} options={baseOptions} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
